import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  User, Phone, Mail, MapPin, Calendar, 
  ArrowLeft, GraduationCap,
  Users, Bus, Building, FileText, Download, Eye
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ChildProfilePage = () => {
  const { childId } = useParams();
  const { getAuthHeader } = useAuth();
  const [child, setChild] = useState(null);
  const [attendance, setAttendance] = useState(null);
  const [fees, setFees] = useState([]);
  const [documents, setDocuments] = useState({ uploaded: [], required: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchChildData();
  }, [childId]);

  const fetchChildData = async () => {
    try {
      // Fetch child details
      const childRes = await axios.get(`${API_URL}/api/students/${childId}`, {
        headers: getAuthHeader()
      });
      setChild(childRes.data);

      // Fetch attendance summary
      try {
        const attendanceRes = await axios.get(`${API_URL}/api/attendance/student/${childId}/summary`, {
          headers: getAuthHeader()
        });
        setAttendance(attendanceRes.data);
      } catch (e) {
        console.warn('Attendance data not available');
      }

      // Fetch fee status
      try {
        const feesRes = await axios.get(`${API_URL}/api/fee-management/student/${childId}/fees`, {
          headers: getAuthHeader()
        });
        setFees(feesRes.data?.items || feesRes.data || []);
      } catch (e) {
        console.warn('Fee data not available');
      }

      // Fetch documents
      try {
        const docsRes = await axios.get(`${API_URL}/api/documents/student/${childId}`, {
          headers: getAuthHeader()
        });
        setDocuments({
          uploaded: docsRes.data?.uploaded_documents || [],
          required: docsRes.data?.required_documents || []
        });
      } catch (e) {
        console.warn('Documents data not available');
      }
    } catch (error) {
      console.error('Failed to fetch child data:', error);
      toast.error('Failed to load child profile');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout title="Child Profile">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  if (!child) {
    return (
      <DashboardLayout title="Child Profile">
        <div className="text-center py-12">
          <p className="text-muted-foreground">Child not found</p>
          <Link to="/parent">
            <Button variant="outline" className="mt-4">
              <ArrowLeft className="h-4 w-4 mr-2" /> Back to Dashboard
            </Button>
          </Link>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Child Profile">
      <div className="space-y-6">
        {/* Back Button */}
        <Link to="/parent">
          <Button variant="outline" size="sm">
            <ArrowLeft className="h-4 w-4 mr-2" /> Back to Dashboard
          </Button>
        </Link>

        {/* Profile Header */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
              {/* Avatar */}
              <div className="w-24 h-24 rounded-full bg-primary/10 flex items-center justify-center">
                <User className="h-12 w-12 text-primary" />
              </div>
              
              {/* Basic Info */}
              <div className="flex-1">
                <h1 className="text-2xl font-bold">{child.name}</h1>
                <div className="flex flex-wrap gap-2 mt-2">
                  <Badge variant="outline">
                    <GraduationCap className="h-3 w-3 mr-1" />
                    {child.class_name || 'N/A'}
                  </Badge>
                  <Badge variant="outline">
                    Roll: {child.roll_number || 'N/A'}
                  </Badge>
                  <Badge variant="secondary">
                    {child.student_id || child.id?.slice(0, 8)}
                  </Badge>
                  {child.is_hosteller && (
                    <Badge className="bg-purple-500">
                      <Building className="h-3 w-3 mr-1" />
                      Hosteller
                    </Badge>
                  )}
                  {child.transport_route && (
                    <Badge className="bg-orange-500">
                      <Bus className="h-3 w-3 mr-1" />
                      Transport
                    </Badge>
                  )}
                </div>
              </div>

              {/* Quick Stats */}
              <div className="grid grid-cols-2 gap-4 text-center">
                <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                  <p className="text-2xl font-bold text-green-600">
                    {attendance?.attendance_rate || attendance?.attendance_percentage || 0}%
                  </p>
                  <p className="text-xs text-muted-foreground">Attendance</p>
                </div>
                <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                  <p className="text-2xl font-bold text-blue-600">
                    ₹{fees.reduce((sum, f) => sum + (f.pending_amount || 0), 0)}
                  </p>
                  <p className="text-xs text-muted-foreground">Pending Fees</p>
                </div>
              </div>
            </div>

            {/* Transport & Hostel Info */}
            {(child.transport_route || child.is_hosteller) && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4 pt-4 border-t">
                {child.transport_route && (
                  <div className="flex items-center gap-3 p-3 bg-orange-50 dark:bg-orange-900/20 rounded-lg">
                    <Bus className="h-5 w-5 text-orange-500" />
                    <div>
                      <p className="text-sm text-muted-foreground">Transport</p>
                      <p className="font-medium">{child.transport_route}</p>
                      {child.transport_stop && (
                        <p className="text-xs text-muted-foreground">Stop: {child.transport_stop}</p>
                      )}
                    </div>
                  </div>
                )}
                {child.is_hosteller && (
                  <div className="flex items-center gap-3 p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                    <Building className="h-5 w-5 text-purple-500" />
                    <div>
                      <p className="text-sm text-muted-foreground">Hostel</p>
                      <p className="font-medium">{child.hostel_building || 'Assigned'}</p>
                      {child.hostel_room && (
                        <p className="text-xs text-muted-foreground">Room: {child.hostel_room}</p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Tabs */}
        <Tabs defaultValue="details" className="w-full">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="details">Details</TabsTrigger>
            <TabsTrigger value="attendance">Attendance</TabsTrigger>
            <TabsTrigger value="fees">Fees</TabsTrigger>
            <TabsTrigger value="documents">Documents</TabsTrigger>
          </TabsList>

          {/* Details Tab */}
          <TabsContent value="details" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Personal Information</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-center gap-3 p-3 bg-muted rounded-lg">
                    <Calendar className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <p className="text-sm text-muted-foreground">Date of Birth</p>
                      <p className="font-medium">{child.date_of_birth || 'N/A'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-muted rounded-lg">
                    <User className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <p className="text-sm text-muted-foreground">Gender</p>
                      <p className="font-medium capitalize">{child.gender || 'N/A'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-muted rounded-lg">
                    <Users className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <p className="text-sm text-muted-foreground">Blood Group</p>
                      <p className="font-medium">{child.blood_group || 'N/A'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-muted rounded-lg">
                    <Mail className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <p className="text-sm text-muted-foreground">Email</p>
                      <p className="font-medium">{child.email || 'N/A'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-muted rounded-lg col-span-full">
                    <MapPin className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <p className="text-sm text-muted-foreground">Address</p>
                      <p className="font-medium">{child.address || 'N/A'}</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Guardian Info */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Guardian Information</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Primary Parent/Guardian */}
                  {(child.parent_name || child.guardian_name) && (
                    <div className="flex items-center gap-3 p-3 bg-primary/5 rounded-lg col-span-full">
                      <Users className="h-5 w-5 text-primary" />
                      <div>
                        <p className="text-sm text-muted-foreground">Primary Guardian</p>
                        <p className="font-medium">{child.parent_name || child.guardian_name}</p>
                        {child.parent_email && (
                          <p className="text-xs text-muted-foreground">{child.parent_email}</p>
                        )}
                      </div>
                    </div>
                  )}
                  
                  <div className="flex items-center gap-3 p-3 bg-muted rounded-lg">
                    <User className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <p className="text-sm text-muted-foreground">Father Name</p>
                      <p className="font-medium">{child.father_name || child.parent_name || 'N/A'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-muted rounded-lg">
                    <Phone className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <p className="text-sm text-muted-foreground">Father Phone</p>
                      <p className="font-medium">{child.father_phone || child.guardian_phone || 'N/A'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-muted rounded-lg">
                    <User className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <p className="text-sm text-muted-foreground">Mother Name</p>
                      <p className="font-medium">{child.mother_name || 'N/A'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-muted rounded-lg">
                    <Phone className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <p className="text-sm text-muted-foreground">Mother Phone</p>
                      <p className="font-medium">{child.mother_phone || 'N/A'}</p>
                    </div>
                  </div>
                  
                  {/* Emergency Contact */}
                  {child.emergency_contact && (
                    <div className="flex items-center gap-3 p-3 bg-red-50 dark:bg-red-900/20 rounded-lg col-span-full">
                      <Phone className="h-5 w-5 text-red-500" />
                      <div>
                        <p className="text-sm text-red-600">Emergency Contact</p>
                        <p className="font-medium">{child.emergency_contact}</p>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Attendance Tab */}
          <TabsContent value="attendance">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Attendance Summary</CardTitle>
              </CardHeader>
              <CardContent>
                {attendance ? (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="text-center p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
                      <p className="text-3xl font-bold text-green-600">{attendance.present || attendance.present_days || 0}</p>
                      <p className="text-sm text-muted-foreground">Present</p>
                    </div>
                    <div className="text-center p-4 bg-red-50 dark:bg-red-900/20 rounded-lg">
                      <p className="text-3xl font-bold text-red-600">{attendance.absent || attendance.absent_days || 0}</p>
                      <p className="text-sm text-muted-foreground">Absent</p>
                    </div>
                    <div className="text-center p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
                      <p className="text-3xl font-bold text-yellow-600">{attendance.late || attendance.late_days || 0}</p>
                      <p className="text-sm text-muted-foreground">Late</p>
                    </div>
                    <div className="text-center p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                      <p className="text-3xl font-bold text-blue-600">{attendance.total_days || 0}</p>
                      <p className="text-sm text-muted-foreground">Total Days</p>
                    </div>
                  </div>
                ) : (
                  <p className="text-center text-muted-foreground py-8">No attendance data available</p>
                )}
                
                <div className="mt-4 text-center">
                  <Link to="/parent/attendance">
                    <Button variant="outline">View Detailed Attendance</Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Fees Tab */}
          <TabsContent value="fees">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Fee Status</CardTitle>
              </CardHeader>
              <CardContent>
                {fees.length > 0 ? (
                  <div className="space-y-3">
                    {fees.map((fee, idx) => (
                      <div key={idx} className="flex justify-between items-center p-4 border rounded-lg">
                        <div>
                          <p className="font-medium">{fee.fee_head_name || fee.name}</p>
                          <p className="text-sm text-muted-foreground">
                            Due: {fee.due_date || 'N/A'}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="font-bold">₹{fee.amount || 0}</p>
                          <Badge variant={fee.status === 'paid' ? 'default' : 'destructive'}>
                            {fee.status || 'pending'}
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-muted-foreground py-8">No fee records found</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Documents Tab */}
          <TabsContent value="documents">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  Uploaded Documents
                </CardTitle>
              </CardHeader>
              <CardContent>
                {documents.uploaded.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {documents.uploaded.map((doc, idx) => (
                      <div key={idx} className="flex items-center justify-between p-4 border rounded-lg bg-muted/50">
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-primary/10 rounded">
                            <FileText className="h-5 w-5 text-primary" />
                          </div>
                          <div>
                            <p className="font-medium">{doc.document_type || doc.name}</p>
                            <p className="text-xs text-muted-foreground">
                              Uploaded: {doc.uploaded_at ? new Date(doc.uploaded_at).toLocaleDateString() : 'N/A'}
                            </p>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          {doc.file_url && (
                            <>
                              <Button size="sm" variant="outline" asChild>
                                <a href={doc.file_url} target="_blank" rel="noopener noreferrer">
                                  <Eye className="h-4 w-4" />
                                </a>
                              </Button>
                              <Button size="sm" variant="outline" asChild>
                                <a href={doc.file_url} download>
                                  <Download className="h-4 w-4" />
                                </a>
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <FileText className="h-12 w-12 mx-auto mb-2 opacity-50" />
                    <p>No documents uploaded yet</p>
                  </div>
                )}

                {/* Required Documents Status */}
                {documents.required.length > 0 && (
                  <div className="mt-6">
                    <h4 className="font-medium mb-3">Required Documents</h4>
                    <div className="space-y-2">
                      {documents.required.map((doc, idx) => {
                        const isUploaded = documents.uploaded.some(
                          u => u.document_type === doc.type || u.type === doc.type
                        );
                        return (
                          <div key={idx} className="flex items-center justify-between p-3 border rounded-lg">
                            <span>{doc.label || doc.type}</span>
                            <Badge variant={isUploaded ? 'default' : 'secondary'}>
                              {isUploaded ? 'Uploaded' : 'Pending'}
                            </Badge>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
};

export default ChildProfilePage;
